/**
 * Old Attribute analysis links → the rebuilt screen (design: "Redirects keep
 * every old link working"). Pure, so the router and the tests share it.
 *
 *   /attribute-analysis                       → /attribute-insights
 *   /attribute-analysis?attribute_filter_key|attribute|value=…
 *                                             → /attribute-insights/explore
 *   /attribute-analysis/explore?…             → /attribute-insights/explore
 *                                               (legacy attribute_filter_key/value
 *                                               read as attribute/value, then dropped)
 *   /attribute-analysis/comparison?comparison&scope
 *                                             → /attribute-insights with the
 *                                               scope applied to the page, the
 *                                               selection's metric, and the
 *                                               drawer open
 *
 * Every other key survives untouched; the screen's own parser drops what it
 * does not offer (thresholds, activity), exactly like an invalid value.
 */
import { parseSearch, stringifySearch } from '@/router/search';

export const INSIGHTS_PATH = '/attribute-insights';
export const EXPLORE_PATH = '/attribute-insights/explore';

type Query = Record<string, string | string[]>;

const href = (path: string, query: Query, hash = ''): string => `${path}${stringifySearch(query)}${hash ? `#${hash}` : ''}`;

/** attribute_filter_key/value → attribute/value (an explicit new key wins). */
function withoutLegacyKeys(query: Query): Query {
  const { attribute_filter_key: key, attribute_filter_value: value, ...rest } = query;
  const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v);
  const out: Query = { ...rest };
  if (out.attribute === undefined && first(key)) out.attribute = first(key) as string;
  if (out.value === undefined && first(value) !== undefined && first(value) !== '') out.value = first(value) as string;
  return out;
}

export const hasLegacyKeys = (searchStr: string): boolean => {
  const query = parseSearch(searchStr);
  return query.attribute_filter_key !== undefined || query.attribute_filter_value !== undefined;
};

/** `/attribute-analysis?…`: a subject in the query belongs on By attribute. */
export function legacyInsightsHref(searchStr: string, hash = ''): string {
  const query = parseSearch(searchStr);
  const namesSubject = query.attribute_filter_key !== undefined || query.attribute !== undefined || query.value !== undefined;
  return namesSubject ? href(EXPLORE_PATH, withoutLegacyKeys(query), hash) : href(INSIGHTS_PATH, query, hash);
}

/** `/attribute-analysis/explore?…` and a legacy-keyed `/attribute-insights/explore?…`. */
export const legacyExploreHref = (searchStr: string, hash = ''): string => href(EXPLORE_PATH, withoutLegacyKeys(parseSearch(searchStr)), hash);

/** Scope keys an apps/web `scope` JSON (PerformanceFilterValues) can carry that this screen reads. */
const SCOPE_LISTS = ['app_id', 'asset_type', 'campaign_name', 'ad_group_name', 'ad_group_ad_name', 'ad_network_account_id', 'country_code', 'platform', 'os'] as const;

const asStrings = (value: unknown): string[] =>
  (Array.isArray(value) ? value : value === undefined || value === null ? [] : [value])
    .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
    .map(String);

/**
 * `/attribute-analysis/comparison?comparison={selection}&scope={filters}`.
 * The scope (apps, dates, filters) becomes the page's own scope, so the page
 * behind the drawer shows what the comparison was computed on. A missing or
 * unreadable scope leaves the page on the user's Focus; a missing selection
 * lands on the feed without a drawer.
 */
export function legacyComparisonHref(searchStr: string, hash = ''): string {
  const query = parseSearch(searchStr);
  const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v);
  const comparison = first(query.comparison);
  const out: Query = {};
  let scope: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(first(query.scope) ?? '');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) scope = parsed as Record<string, unknown>;
  } catch {
    scope = {};
  }
  // In the screen's canonical key order: apps, dates, then filters.
  const apps = asStrings(scope.app_id);
  if (apps.length) out.app_id = apps;
  const from = typeof scope.date_from === 'string' ? scope.date_from : undefined;
  const to = typeof scope.date_to === 'string' ? scope.date_to : undefined;
  if (from && to) {
    out.date_from = from;
    out.date_to = to;
  }
  for (const key of SCOPE_LISTS) {
    const values = asStrings(scope[key]);
    if (key !== 'app_id' && values.length) out[key] = values;
  }
  if (comparison) {
    try {
      const selection = JSON.parse(comparison) as { metric?: unknown };
      if (typeof selection.metric === 'string' && selection.metric) out.metric = selection.metric;
    } catch {
      // An unreadable selection still opens the drawer, which says so.
    }
    out.comparison = comparison;
  }
  return href(INSIGHTS_PATH, out, hash);
}
